import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';

import LabelFormsSection from './labelFormsSection';
import PalletLabelsSection from './palletLabelsSection';

const VIEW_KEY = 'xms_mis_labels_view';
const readView = () => { try { return localStorage.getItem(VIEW_KEY) === 'pallets' ? 'pallets' : 'forms'; } catch (_) { return 'forms'; } };

// MIS -> Labels. Labels are kept in batches: LABEL FORMS, each with its number, date, origin, destination and pallets (the view it opens on).
// The Pallets view is every pallet one by one, in a form or not, as it was before forms existed. Which view was last used is remembered.
export default function LabelsTab({ openPalletId, onOpenPalletConsumed, openFormId, onOpenFormConsumed }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMob = useMediaQuery(theme.breakpoints.down('md'));
  const [view, setViewState] = useState(readView);
  const [newRequest, setNewRequest] = useState(0);
  const [palletToOpen, setPalletToOpen] = useState(null);
  const setView = (v) => { setViewState(v); try { localStorage.setItem(VIEW_KEY, v); } catch (_) { /* a private window */ } };

  // a link to a pallet opens the Pallets view, a link to a form the forms
  useEffect(() => { if (openPalletId) setView('pallets'); }, [openPalletId]);
  useEffect(() => { if (openFormId) setView('forms'); }, [openFormId]);

  const BD = isDark ? 'rgba(255,255,255,0.07)' : theme.palette.divider;
  const CTRL = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';
  const ON = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)';
  const PRI = isDark ? 'rgba(255,255,255,0.87)' : theme.palette.text.primary;
  const TER = isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.45)';
  const switcher = (
    <Box role="tablist" aria-label={t('mis.labelsTab')} data-testid="lbl-view-switch"
      sx={{ display: 'flex', gap: 0.5, bgcolor: CTRL, borderRadius: '9px', p: '3px', border: `1px solid ${BD}`, flexShrink: 0 }}>
      {[
        { id: 'forms', Icon: LibraryBooksOutlinedIcon, labelKey: 'mis.lfViewForms' },
        { id: 'pallets', Icon: LabelOutlinedIcon, labelKey: 'mis.lfViewPallets' },
      ].map(({ id, Icon, labelKey }) => {
        const on = view === id;
        return (
          <ButtonBase key={id} role="tab" aria-selected={on} onClick={() => setView(id)} data-testid={`lbl-view-${id}`}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, height: isMob ? 34 : 24, px: 1, borderRadius: '7px',
              bgcolor: on ? ON : 'transparent', '&:hover': { bgcolor: ON } }}>
            <Icon sx={{ fontSize: 14, color: on ? PRI : TER }} />
            <Typography sx={{ fontSize: isMob ? '0.74rem' : '0.7rem', fontWeight: on ? 700 : 500, color: on ? PRI : TER, whiteSpace: 'nowrap' }}>{t(labelKey)}</Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );

  if (view === 'pallets') {
    return (
      <PalletLabelsSection openId={palletToOpen || openPalletId} onOpenIdConsumed={() => { setPalletToOpen(null); if (onOpenPalletConsumed) onOpenPalletConsumed(); }}
        switcher={switcher} onNew={() => { setView('forms'); setNewRequest((n) => n + 1); }} />
    );
  }
  return (
    <LabelFormsSection openId={openFormId} onOpenIdConsumed={onOpenFormConsumed} switcher={switcher} newRequest={newRequest} onNewHandled={() => setNewRequest(0)}
      onOpenPallet={(id) => { setPalletToOpen(id); setView('pallets'); }} />
  );
}
